import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";

const LINKS = [
  { label: "About", target: "#about", testId: "nav-link-about" },
  { label: "Services", target: "#services", testId: "nav-link-services" },
  { label: "Contact", target: "#contact", testId: "nav-link-contact" },
];

export default function Nav({ onNavigate }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavigate = (target) => {
    setMenuOpen(false);
    onNavigate(target);
  };

  return (
    <header
      data-testid="nav-bar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || menuOpen
          ? "border-b border-white/10 bg-black/60 backdrop-blur-xl"
          : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[90rem] items-center justify-between px-6 py-5 md:px-12">
        <button
          data-testid="nav-logo"
          onClick={() => handleNavigate(0)}
          className="whitespace-nowrap font-serif text-2xl tracking-tight text-fog transition-colors duration-300 hover:text-steel md:text-4xl"
        >
          CallMe📲<span className="italic text-steel">Gerard.com</span>
        </button>
        <nav className="hidden items-center gap-10 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={l.testId}
              onClick={() => handleNavigate(l.target)}
              className="text-xs uppercase tracking-[0.25em] text-ash transition-[color,transform] duration-300 hover:-translate-y-0.5 hover:text-fog"
            >
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a
            data-testid="nav-call-button"
            href="tel:3177076818"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.2em] text-fog transition-[background-color,border-color] duration-300 hover:border-navy hover:bg-navy md:px-5"
          >
            <Phone className="h-3.5 w-3.5" />
            Call Me
          </a>
          <button
            data-testid="nav-menu-toggle"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-fog transition-colors duration-300 hover:border-navy hover:bg-navy md:hidden"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
            className="overflow-hidden border-t border-white/10 bg-black/80 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col px-6 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.label}
                  data-testid={`${l.testId}-mobile`}
                  onClick={() => handleNavigate(l.target)}
                  className="border-b border-white/5 py-4 text-left font-serif text-xl tracking-tight text-fog transition-colors duration-300 hover:text-steel last:border-b-0"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
